import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useCurrentUser } from "../../../../../../hooks/useCurrentUser";
import * as XLSX from "xlsx";
import { KpiIcon } from "../../KpiIcons";
import { apiCall } from "../../../../../../_services/apiCall";
import { uploadFileMetadata } from "../../../../../../_services/documentsService";
import config from "../../../../../../config/config";
import { formatNumber } from "../../../../../../utils/numberFormat";
import {
  isOk,
  isSpreadsheet,
  parseDate,
  getCol,
  fmt,
} from "./utils";
import Fld from "./FieldLabel";
import DocumentViewerPanel from "./DocumentViewerPanel";
import MultiDocumentUploadField from "../../../../components/MultiDocumentUploadField";
import HistoryModal from "./HistoryModal";
import KpiFormFields from "../../KpiFormFields";
import { mergeKpiAndAssetUnits } from "../../assetUnits";
import { Modal, ModalHeader } from "../../../../../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../../../../../globalComponents/ui/Button";


/* ─────────────────────────────────────────────────────────────
   Spark bar (for emission / energy YoY-style rows)
───────────────────────────────────────────────────────────── */
const SparkBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: 9.5, fontFamily: "monospace", color: "#94a3b8", minWidth: 28, textAlign: "right" }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Compact emission / energy inline row (same style as KpiRow)
───────────────────────────────────────────────────────────── */
const CompactTypeRow = ({ icon, label, value, unit, accentColor, bgColor, borderColor }) => {
  const val = Number(value) || 0;
  const hasData = value !== null && value !== undefined;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      background: "#ffffff",
      borderRadius: 8,
      border: `1px solid ${borderColor}`,
      overflow: "hidden",
      flex: 1,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "6px 10px", minWidth: 90, flexShrink: 0,
        background: bgColor,
        borderRight: `1px solid ${borderColor}`,
      }}>
        <span style={{ fontSize: 12 }}>{icon}</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: accentColor, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </span>
      </div>
      <div style={{ flex: 1, padding: "6px 10px" }}>
        {hasData ? (
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "var(--ink)", fontFamily: "monospace" }}>
              {formatNumber(val)}
            </span>
            <span style={{ fontSize: 9, color: "var(--ink4)", textTransform: "uppercase" }}>
              {unit}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>—</span>
        )}
      </div>
    </div>
  );
};

// ── Required Evidences banner ─────────────────────────────────────────────────
function RequiredEvidences({ requiredEvidences }) {
  if (!requiredEvidences || requiredEvidences.length === 0) return null;

  return (
    <div style={{
      background: "var(--bdr2)",
      padding: "12px 24px 14px",
      border: "1px solid var(--bdr)",
      borderRadius: 9
    }}>
      {/* Label row */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--ink2)", letterSpacing: "0.02em" }}>
          Required Evidences
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: "1px 8px", borderRadius: 20,
          background: "var(--blue3)", color: "var(--blue)",
          border: "1px solid var(--blue2)",
          marginLeft: 2,
        }}>
          {requiredEvidences.length}
        </span>
        <span style={{ fontSize: 13, color: "var(--ink4)", marginLeft: "auto" }}>
          ensure your document covers these
        </span>
      </div>

      {/* Evidence pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {requiredEvidences.map((ev, i) => (
          <div key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "var(--card)",
            border: "1px solid var(--bdr)",
            borderRadius: 8, padding: "6px 12px",
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              background: "var(--blue3)",
              border: "1px solid var(--blue2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 800, color: "var(--blue)",
            }}>
              {i + 1}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink2)", lineHeight: 1.3 }}>{ev}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
const AccordionPanel = ({
  answer,
  locs,
  kpiAssets = [],
  questionId,
  financialYearId,
  requiredEvidences,
  onSave,
  onCancel,
  canEdit,
  onChat,
  unitSetting,
  onOpenAuditTrail,
  hasPermission,
  assign_raw_list = [],
  kpiType = null,
}) => {
  const { currentUserId, roleId, allSources } = useCurrentUser();

  const allowedLocations = useMemo(() => {
    let baseList = locs;
    const isRestrictedUser = roleId !== 2 && roleId !== 1;

    // A sub-location option is keyed `sub_<id>`, so it is matched through its
    // parent — Number("sub_5") is NaN and would drop it from the list.
    const parentIdOf = (l) => Number(l.locationId ?? l.id);

    if (isRestrictedUser && Array.isArray(allSources)) {
      baseList = locs.filter(l => allSources.includes(parentIdOf(l)));
    }

    if (!assign_raw_list || !currentUserId) return baseList;

    const assignedScopeKeys = new Set();
    const assignedWholeSites = new Set();
    let hasAssignmentsForUser = false;

    assign_raw_list.forEach(a => {
      const assignee = (a.assignees || []).find(x => String(x.userId) === String(currentUserId));
      const userIds = (a.assignedTo || a.assigned_to || []).map(String);
      if (!assignee && !userIds.includes(String(currentUserId))) return;

      hasAssignmentsForUser = true;

      if (assignee?.scopes?.length) {
        assignee.scopes.forEach(s => {
          if (s.subLocationId) assignedScopeKeys.add(`sub_${s.subLocationId}`);
          else if (s.locationId != null) assignedWholeSites.add(String(s.locationId));
        });
        return;
      }

      const sIds = assignee?.locationIds || a.sourceIds || a.source_ids || [];
      sIds.forEach(id => {
        if (id !== null && id !== undefined) assignedWholeSites.add(String(id));
      });
    });

    if (!hasAssignmentsForUser) return baseList;

    return baseList.filter(l => (
      assignedScopeKeys.has(String(l.id)) || assignedWholeSites.has(String(parentIdOf(l)))
    ));
  }, [locs, assign_raw_list, currentUserId, roleId, allSources]);

  /** The record's own scope key: `sub_<id>` when it holds a sub-location. */
  const currentScopeKey = useMemo(() => {
    const subId = answer.sub_location_id ?? answer.subLocationId ?? null;
    if (subId) return `sub_${subId}`;
    const lid = answer.location_id ?? answer.locationId ?? "";
    return lid ? String(lid) : "";
  }, [answer]);

  /**
   * Label for the record's own scope when `locs` no longer holds it — a
   * sub-location that has since been deleted. The record still carries its
   * parent `location_id`, so the site name resolves from there and is marked as
   * a removed sub-location; the raw `sub_5` key never reaches the UI.
   */
  const currentScopeName = useMemo(() => {
    const match = locs.find(x => String(x.id) === currentScopeKey);
    if (match) return match.name;

    const subId = answer.sub_location_id ?? answer.subLocationId ?? null;
    if (!subId) return `Location ${currentScopeKey}`;

    const parentId = answer.location_id ?? answer.locationId ?? null;
    const parent = parentId == null ? null : locs.find(x => String(x.id) === String(parentId));
    return parent ? `${parent.name} — removed sub-location` : `Removed sub-location ${subId}`;
  }, [answer, locs, currentScopeKey]);

  /**
   * One option per reportable scope, so a record written against KMC-T1 shows
   * KMC-T1 rather than its parent site. The record's own scope is always
   * included: a site that has since been split is no longer offered for new
   * data, and dropping it here would blank the select and wipe the location.
   */
  const locationChoices = useMemo(() => {
    const byId = new Map();

    allowedLocations
      .filter(l => l.assignable !== false)
      .forEach((l) => {
        const id = String(l.id);
        if (!id || id === "null" || id === "undefined") return;
        if (!byId.has(id)) byId.set(id, { id, name: l.name });
      });

    if (currentScopeKey && !byId.has(currentScopeKey)) {
      byId.set(currentScopeKey, { id: currentScopeKey, name: currentScopeName });
    }

    return [...byId.values()];
  }, [allowedLocations, currentScopeKey, currentScopeName]);

  /** Splits a scope key back into the ids the update endpoint expects. */
  const scopeIdsOf = useCallback((scopeKey) => {
    const match = locs.find(l => String(l.id) === String(scopeKey));
    if (match?.locationId) {
      return { locationId: Number(match.locationId), subLocationId: match.subLocationId ?? null };
    }
    const raw = String(scopeKey ?? "");
    if (raw.startsWith("sub_")) return { locationId: null, subLocationId: Number(raw.slice(4)) };
    return { locationId: Number(raw) || null, subLocationId: null };
  }, [locs]);

  const kpiUnitObjs = unitSetting?.supportedUnits ?? [];

  const defaultUnit =
    unitSetting?.selectedUnit ||
    unitSetting?.baseUnit ||
    answer.unit ||
    kpiUnitObjs[0]?.unit ||
    "";

  /**
   * A KPI configured in KPI Unit Settings is reported in that unit only, so the
   * unit picker is replaced by a read-only chip on edit as well — otherwise an
   * edit could move a stored value to a unit the save then rejects.
   */
  const isUnitLocked = unitSetting?.selectedUnitId != null && !!unitSetting?.selectedUnit;
  const lockedUnit = isUnitLocked ? unitSetting.selectedUnit : "";

  /* ── Accepted answers are locked from editing ── */
  const isAccepted = (answer.status || "").toLowerCase() === "approved";
  const subStatus = (answer.submissionStatus || answer.submission_status || "").toLowerCase();
  const canEditRecord = hasPermission("quantitative-data.update") && !isAccepted &&
    (subStatus === "draft" || subStatus === "returned_to_contributor" || !subStatus || hasPermission("quantitative-data.send-to-audit"));

  const [editMode, setEditMode] = useState(false);
  const [showHistModal, setShowHistModal] = useState(false);

  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const [returning, setReturning] = useState(false);
  const [returnError, setReturnError] = useState("");

  const submitForReview = async () => {
    setWorkflowLoading(true);
    setMsg(null);
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${answer.id}/submit-for-review`,
        {},
        {},
        "PATCH"
      );
      if (isOk(res)) {
        setMsg({ ok: true, t: "Submitted for review successfully" });
        onSave?.();
      } else {
        setMsg({ ok: false, t: res?.data?.message || "Submission failed" });
      }
    } catch {
      setMsg({ ok: false, t: "Network error" });
    } finally {
      setWorkflowLoading(false);
    }
  };

  const sendToAudit = async () => {
    setWorkflowLoading(true);
    setMsg(null);
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${answer.id}/send-to-audit`,
        {},
        { questionType: 'quantitative' },
        "PATCH"
      );
      if (isOk(res)) {
        setMsg({ ok: true, t: "Sent for audit successfully" });
        onSave?.();
      } else {
        setMsg({ ok: false, t: res?.data?.message || "Operation failed" });
      }
    } catch {
      setMsg({ ok: false, t: "Network error" });
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleReturnConfirm = async () => {
    if (!returnComment.trim()) {
      setReturnError("Please enter a reason for returning the record.");
      return;
    }
    setReturning(true);
    setReturnError("");
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${answer.id}/return-to-contributor`,
        {},
        { comment: returnComment.trim() },
        "PATCH"
      );
      if (isOk(res)) {
        setShowReturnModal(false);
        setReturnComment("");
        setMsg({ ok: true, t: "Returned for correction successfully" });
        onSave?.();
      } else {
        setReturnError(res?.data?.message || "Operation failed. Please try again.");
      }
    } catch {
      setReturnError("Network error. Please try again.");
    } finally {
      setReturning(false);
    }
  };

  const [val, setVal] = useState(String(answer.value ?? ""));
  const [unit, setUnit] = useState(isUnitLocked ? lockedUnit : (answer.unit ?? defaultUnit));
  // Holds a scope key (`sub_<id>` for a sub-location), not a bare location id,
  // so the select can show KMC-T1 rather than its parent site.
  const [loc, setLoc] = useState(
    (answer.sub_location_id ?? answer.subLocationId)
      ? `sub_${answer.sub_location_id ?? answer.subLocationId}`
      : String(answer.location_id ?? answer.locationId ?? ""),
  );
  const [utilityType, setutilityType] = useState(answer.utility_type);
  const [from, setFrom] = useState((answer.from_date || answer.fromDate || "")?.slice(0, 10));
  const [to, setTo] = useState((answer.to_date || answer.toDate || "")?.slice(0, 10));
  const [note, setNote] = useState(answer.note ?? answer.remarks ?? "");
  const [kpiAssetId, setKpiAssetId] = useState(answer.kpiAssetId ?? answer.kpi_asset_id ?? null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadedMeta, setUploadedMeta] = useState(null);
  const [uploadErr, setUploadErr] = useState("");
  // Extra evidence attachments beyond the single FileUploadZone slot above —
  // seeded with this answer's currently-attached documents so editing always
  // resends the full desired end-state (documentIds + uploadedDocuments).
  const [attachments, setAttachments] = useState({ documentIds: [], uploadedDocuments: [] });
  // When false, no document in the final attachment set is sent for AI parsing —
  // the backend marks all of them isVerified immediately instead, trusting the
  // manually-entered value since there's nothing to reconcile it against.
  const [shouldParse, setShouldParse] = useState(true);

  // ── KPI form schemas & data ───────────────────────────────────────────────
  // kpiFormSchemas: [{ kpi, fields }, ...]
  // kpiFormData:    { [kpi]: { [fieldKey]: value | { value, unit } } }
  const [kpiFormSchemas, setKpiFormSchemas] = useState([]);
  const [kpiFormData, setKpiFormData] = useState(() => {
    // Seed from the answer's existing form_data (already { [kpi]: { ... } })

    const existing = answer?.formData ?? answer?.form_data ?? null;
    return (existing && typeof existing === "object") ? { ...existing } : {};
  });
  const [unitMaster, setUnitMaster] = useState([]); // [{ id, unit, unit_name }]

  // Fetch unit master + schemas in parallel on mount
  useEffect(() => {
    // Fetch unit master
    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getUnitMaster`,
      {}, {}, "GET", false, false
    ).then(({ isSuccess, data }) => {
      if (isSuccess) {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setUnitMaster(list);
      }
    }).catch(() => { });

    // Fetch schemas for all kpi_types in parallel
    const rawTypes = Array.isArray(kpiType) ? kpiType : kpiType ? [kpiType] : [];
    if (!rawTypes.length) return;

    Promise.allSettled(
      rawTypes.map((kpi) =>
        apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/kpi-form-schema/${kpi}`,
          {}, {}, "GET", false, false
        )
      )
    ).then((results) => {
      const schemas = [];
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const { isSuccess, data } = result.value ?? {};
        const entity = data?.data ?? data;
        if (isSuccess && entity?.isEnabled && Array.isArray(entity?.form)) {
          schemas.push({ kpi: entity.kpi, fields: entity.form });
        }
      });
      setKpiFormSchemas(schemas);
    });
  }, [kpiType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-init base unit for unit-bearing fields once both schemas & unitMaster loaded
  useEffect(() => {
    if (!kpiFormSchemas.length || !unitMaster.length) return;
    setKpiFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      kpiFormSchemas.forEach((schema) => {
        schema.fields.forEach((field) => {
          const hasUnits = Array.isArray(field.supportedUnits) && field.supportedUnits.length > 0;
          if (!hasUnits) return;
          const existing = next[schema.kpi]?.[field.key];
          const alreadyHasUnit =
            existing && typeof existing === "object" && "unitId" in existing && existing.unitId;
          if (alreadyHasUnit) return;
          const baseUnitId = field.supportedUnits.find(su => su.is_base)?.unit_id;
          if (!baseUnitId) return;
          const currentVal =
            existing && typeof existing === "object" && "value" in existing
              ? (existing.value ?? "")
              : (existing ?? "");
          if (!next[schema.kpi]) next[schema.kpi] = {};
          next[schema.kpi] = {
            ...next[schema.kpi],
            [field.key]: { value: currentVal, unitId: baseUnitId },
          };
          changed = true;
        });
      });
      return changed ? next : prev;
    });
  }, [kpiFormSchemas, unitMaster]); // eslint-disable-line

  /* ── location: robust lookup ── */
  const locObj = (() => {
    const lid = loc || currentScopeKey;
    if (!lid) return null;
    return locs.find((l) => String(l.id) === String(lid)) || null;
  })();

  /* ── KPI asset helpers ── */
  const hasKpiAssets = kpiAssets.length > 0;
  // Assets are pinned to a location AND a sub-location, so a KMC-T1 record must
  // not offer KMC-T2's assets. `loc` is a scope key, never a bare location id.
  const selectedScopeIds = scopeIdsOf(loc || currentScopeKey);
  const assetOptionsForLocation = loc
    ? kpiAssets.filter((a) => (
      String(a.sourceId) === String(selectedScopeIds.locationId) &&
      String(a.subLocationId ?? a.sub_location_id ?? "") === String(selectedScopeIds.subLocationId ?? "")
    ))
    : kpiAssets;
  const selectedAssetObj = kpiAssetId
    ? kpiAssets.find((a) => String(a.id) === String(kpiAssetId)) ?? null
    : null;
  const selectedAssetTitle =
    selectedAssetObj?.assetTitle ||

    answer?.kpiAsset?.assetTitle ||
    answer?.kpiAsset?.asset_title ||
    answer?.kpiAssetTitle ||
    "";

  /* ── unit options: the KPI's configured unit when locked, otherwise the
       KPI's units + the selected asset's own units ── */
  const resolvedUnitObjs = isUnitLocked
    ? [kpiUnitObjs.find((u) => Number(u.unit_id) === Number(unitSetting.selectedUnitId))
      ?? { unit_id: unitSetting.selectedUnitId, unit: lockedUnit, factor: 1 }]
    : mergeKpiAndAssetUnits(kpiUnitObjs, selectedAssetObj, unitMaster);
  const resolvedUnits = resolvedUnitObjs.map((u) => u.unit);

  /**
   * True when a row saved before the KPI's unit was fixed still holds a
   * different unit. Editing it stores the configured unit, so the value has to
   * be restated in that unit — silently relabelling the old number would
   * corrupt the figure, hence the warning shown while editing.
   */
  const storedUnitDiffers =
    isUnitLocked && !!answer.unit && answer.unit !== lockedUnit;

  /* ── emission / energy from API response ── */
  const emissionVal = answer.emission_value ?? null;
  const emissionUnit = answer.emission_unit || "kgCO2e";
  const energyVal = answer.energy_value ?? null;
  const energyUnit = answer.energy_unit || "GJ";
  const noxVal = answer.nox_value ?? null;
  const noxUnit = answer.nox_unit || "mg/Nm³";
  const soxVal = answer.sox_value ?? null;
  const soxUnit = answer.sox_unit || "mg/Nm³";
  const ppmVal = answer.ppm_value ?? null;
  const ppmUnit = answer.ppm_unit || "mg/Nm³";

  const hasEmission = kpiType?.includes("EMISSION");
  const hasEnergy = kpiType?.includes("ENERGY");
  const hasNOx = kpiType?.includes("NOx");
  const hasSOx = kpiType?.includes("SOx");
  const hasPPM = kpiType?.includes("PPM");

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Build form_data payload — iterate all schemas, build nested { [kpi]: { [fieldKey]: value | { value, unit } } }
      let formDataPayload = null;
      if (kpiFormSchemas.length > 0) {
        const nested = {};
        kpiFormSchemas.forEach((schema) => {
          const schemaData = kpiFormData[schema.kpi] ?? {};
          const hasAnyValue = Object.values(schemaData).some((v) => {
            if (v == null || v === "") return false;
            if (typeof v === "object" && "value" in v) return v.value !== "" && v.value != null;
            return true;
          });
          if (hasAnyValue) {
            const cleaned = {};
            schema.fields.forEach((field) => {
              const raw = schemaData[field.key];
              if (raw == null || raw === "") return;
              const hasUnits = Array.isArray(field.supportedUnits) && field.supportedUnits.length > 0;
              if (hasUnits && typeof raw === "object" && "value" in raw) {
                const numVal = raw.value !== "" && raw.value != null ? parseFloat(String(raw.value)) : null;
                if (numVal != null && !isNaN(numVal)) {
                  cleaned[field.key] = { value: numVal, unitId: raw.unitId ?? "" };
                }
              } else if (!hasUnits) {
                cleaned[field.key] = field.type === "number" ? parseFloat(raw) : raw;
              }
            });
            if (Object.keys(cleaned).length > 0) nested[schema.kpi] = cleaned;
          }
        });
        formDataPayload = Object.keys(nested).length > 0 ? nested : null;
      }

      const payload = {
        value: Number(val),
        unit: resolvedUnits.length > 0 ? (unit || null) : null,
        unit_id: resolvedUnits.length > 0 ? (resolvedUnitObjs.find(u => u.unit === unit)?.unit_id ?? null) : null,
        // `loc` is a scope key — split it so the record keeps its sub-location.
        // An explicit null is required to move a record back to site level;
        // omitting the field makes the backend keep the previous sub-location.
        ...(loc
          ? {
            location_id: selectedScopeIds.locationId ?? undefined,
            sub_location_id: selectedScopeIds.subLocationId ?? null,
          }
          : {}),
        from_date: from || undefined,
        to_date: to || undefined,
        utility_type: utilityType,
        ...(kpiAssetId ? { kpiAssetId: Number(kpiAssetId) } : {}),
        documentIds: attachments.documentIds,
        uploadedDocuments: [
          ...(uploadedMeta?.fileMetadataId ? [{
            documentType: uploadedMeta.documentType,
            fileMetadataId: uploadedMeta.fileMetadataId,
            fileMetadata: uploadedMeta.fileMetadata,
            isExisting: uploadedMeta.isExisting,
          }] : []),
          ...attachments.uploadedDocuments,
        ],
        shouldParse,
        ...(kpiFormSchemas.length > 0 ? { form_data: formDataPayload } : {}),
      };
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantitative-data/${answer.id}`, {}, payload, "PUT"
      );
      if (isOk(res)) {
        setMsg({ ok: true, t: "Saved successfully" });
        setEditMode(false); onSave?.();
        setTimeout(() => setMsg(null), 3000);
      } else {
        setMsg({ ok: false, t: res?.data?.message || "Save failed" });
      }
    } catch { setMsg({ ok: false, t: "Network error" }); }
    setSaving(false);
  };

  // ── Cancel edit — resets fields but does NOT close the accordion ─────────
  const cancelEdit = () => {
    setEditMode(false);
    setVal(String(answer.value ?? ""));
    setUnit(isUnitLocked ? lockedUnit : (answer.unit ?? defaultUnit));
    setLoc(currentScopeKey);
    setFrom((answer.from_date || answer.fromDate || "")?.slice(0, 10));
    setTo((answer.to_date || answer.toDate || "")?.slice(0, 10));
    setNote(answer.note ?? answer.remarks ?? "");
    setutilityType(answer.utility_type);
    setUploadedMeta(null); setUploadErr(""); setMsg(null);
    setKpiAssetId(answer.kpiAssetId ?? answer.kpi_asset_id ?? null);
    setShouldParse(true);
    // Reset kpiFormData to original answer values (already nested { [kpi]: { ... } })
    const existing = answer?.formData ?? answer?.form_data ?? null;
    setKpiFormData((existing && typeof existing === "object") ? { ...existing } : {});
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="ahp-down"
      style={{
        padding: "12px 20px 16px",
        background: "linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)",
        borderTop: "2px solid var(--blue2)",
      }}
    >
      {/* ── Action bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        marginBottom: 12, paddingBottom: 10,
        borderBottom: "1px solid var(--bdr2)", flexWrap: "wrap",
      }}>
        {/* Edit button hidden for accepted answers */}
        {console.log('has permission', hasPermission("EDIT_KPI_DATA_RECORD"), canEdit, !isAccepted, !editMode)}
        {canEditRecord && (!editMode ? (
          <button className="ahp-btn ahp-btn-amber ahp-btn-sm" onClick={() => setEditMode(true)}>
            <KpiIcon name="audit" size={12} color="var(--amber)" /> Edit Answer
          </button>
        ) : (
          <>
            <button className="ahp-btn ahp-btn-p ahp-btn-sm" onClick={save} disabled={saving || uploading}>
              {saving
                ? <><span className="ahp-spin" style={{ width: 11, height: 11, border: "2px solid #ffffff44", borderTopColor: "#fff" }} /> Saving…</>
                : <><KpiIcon name="check" size={12} color="#fff" /> Save Changes</>}
            </button>
            <button className="ahp-btn ahp-btn-g ahp-btn-sm" onClick={cancelEdit}>Cancel</button>
          </>
        ))}

        {!editMode && !isAccepted && (
          <>
            {hasPermission("quantitative-data.submit-for-review") &&
              !hasPermission("quantitative-data.send-to-audit") &&
              (subStatus === "draft" || subStatus === "returned_to_contributor" || !subStatus) && (
                <button
                  className="ahp-btn ahp-btn-p ahp-btn-sm"
                  onClick={submitForReview}
                  disabled={workflowLoading}
                  style={{ background: "var(--accent)", borderColor: "var(--accent)" }}
                >
                  {workflowLoading ? "Processing…" : "Submit for Review"}
                </button>
              )}

            {hasPermission("quantitative-data.send-to-audit") &&
              (subStatus === "draft" || subStatus === "returned_to_contributor" || !subStatus) && (
                <button
                  className="ahp-btn ahp-btn-p ahp-btn-sm"
                  onClick={sendToAudit}
                  disabled={workflowLoading}
                  style={{ background: "var(--green)", borderColor: "var(--green)" }}
                >
                  {workflowLoading ? "Processing…" : "Send for Audit"}
                </button>
              )}

            {subStatus === "submitted_for_review" && (
              <>
                {hasPermission("quantitative-data.send-to-audit") && (
                  <button
                    className="ahp-btn ahp-btn-p ahp-btn-sm"
                    onClick={sendToAudit}
                    disabled={workflowLoading}
                    style={{ background: "var(--green)", borderColor: "var(--green)" }}
                  >
                    {workflowLoading ? "Processing…" : "Send for Audit"}
                  </button>
                )}
                {hasPermission("quantitative-data.return-to-contributor") && (
                  <button
                    className="ahp-btn ahp-btn-amber ahp-btn-sm"
                    onClick={() => {
                      setReturnComment("");
                      setReturnError("");
                      setShowReturnModal(true);
                    }}
                    disabled={workflowLoading}
                  >
                    {workflowLoading ? "Processing…" : "Return for Correction"}
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* Accepted lock badge */}
        {isAccepted && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 10.5, fontWeight: 700, color: "var(--green)",
            background: "#dcfce7", border: "1px solid #bbf7d0",
            borderRadius: 5, padding: "3px 9px",
          }}>
            <KpiIcon name="check" size={11} color="var(--green)" /> Accepted · read-only
          </span>
        )}

        <div style={{ width: 1, height: 20, background: "var(--bdr)", flexShrink: 0 }} />

        <button className="ahp-btn ahp-btn-g ahp-btn-sm" onClick={() => setShowHistModal(true)}>
          <KpiIcon name="history" size={12} color="var(--ink3)" /> View History
        </button>

        <button
          className="ahp-btn ahp-btn-g ahp-btn-sm"
          onClick={() => onOpenAuditTrail?.()}
          title="View audit trail for this answer"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, borderColor: "var(--blue2)", color: "var(--blue)", background: "var(--blue3)" }}
        >
          <KpiIcon name="audit" size={12} color="var(--blue)" /> Audit Trail
        </button>

        {msg && (
          <span style={{ fontSize: 11.5, fontWeight: 600, color: msg.ok ? "var(--green)" : "var(--red)" }}>
            {msg.ok ? "✓" : "⚠"} {msg.t}
          </span>
        )}

        {!editMode && !isAccepted && (
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "var(--ink4)", fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--amber)", display: "inline-block", flexShrink: 0 }} />
            View only · click Edit Answer to make changes
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

        {/* ── Row 1: Location | KPI Asset | Date Range ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {/* Location */}
          <Fld label="Location">
            <select
              className="ahp-sel ahp-input"
              value={loc}
              onChange={(e) => setLoc(e.target.value)}
              style={{ width: "100%" }}
              disabled={!editMode}
            >
              <option value="">— No location —</option>
              {locationChoices.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Fld>


          {/* KPI Asset (only when this KPI has assets) */}
          {hasKpiAssets && (
            <Fld label="KPI Asset">
              {!editMode ? (

                selectedAssetTitle ? (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                    color: "var(--blue)", background: "var(--blue3)",
                    border: "1px solid var(--blue2)",
                    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    🏭 {selectedAssetTitle}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--ink4)", fontStyle: "italic" }}>No asset linked</span>
                )
              ) : (
                assetOptionsForLocation.length === 0 ? (
                  <div style={{
                    padding: "8px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500,
                    background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c",
                  }}>
                    No assets for this location
                  </div>
                ) : (
                  <select
                    className="ahp-sel ahp-input"
                    value={kpiAssetId ?? ""}
                    onChange={(e) => setKpiAssetId(e.target.value ? Number(e.target.value) : null)}
                    style={{ width: "100%" }}
                  >
                    <option value="">— Select asset —</option>
                    {assetOptionsForLocation.map((a) => (
                      <option key={a.id} value={a.id}>{a.assetTitle}</option>
                    ))}
                  </select>
                )
              )}
            </Fld>
          )}

          {/* Date Range — From + To side by side */}
          <Fld label="Date Range">
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                className="ahp-input"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={!editMode}
                style={{ flex: 1, minWidth: 0 }}
              />
              <span style={{ fontSize: 11, color: "var(--ink4)", flexShrink: 0 }}>→</span>
              <input
                className="ahp-input"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={!editMode}
                style={{ flex: 1, minWidth: 0 }}
              />
            </div>
          </Fld>
        </div>

        {/* ── Row 2: Value | Unit | Utility Type ── */}
        <div style={{ display: "grid", gridTemplateColumns: resolvedUnits.length > 0 ? "1fr 140px 1fr" : "1fr 1fr", gap: 8 }}>
          <Fld label="Value">
            <input
              className="ahp-input"
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="Enter value…"
              disabled={!editMode}
            />
          </Fld>
          {resolvedUnits.length > 0 && (
            <Fld label="Unit">
              {isUnitLocked ? (
                <div
                  className="ahp-input"
                  title="Fixed by Settings → KPI Unit Settings"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: "100%", fontWeight: 700, cursor: "default", userSelect: "none",
                    background: "var(--surface-2, #f4f6f8)",
                  }}
                >
                  {lockedUnit}
                </div>
              ) : (
                <select
                  className="ahp-sel ahp-input"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  style={{ width: "100%" }}
                  disabled={!editMode}
                >
                  {resolvedUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
            </Fld>
          )}
          <Fld label="Utility Type">
            <select
              className="ahp-sel ahp-input"
              value={utilityType || "exclusive"}
              onChange={(e) => setutilityType(e.target.value)}
              disabled={!editMode}
            >
              <option value="exclusive">Exclusive</option>
              <option value="shared">Shared</option>
            </select>
          </Fld>
        </div>

        {editMode && storedUnitDiffers && (
          <div style={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
            Recorded in {answer.unit}, but this KPI is now fixed to {lockedUnit}. Saving stores
            the value as {lockedUnit} — restate it in {lockedUnit} before saving.
          </div>
        )}

        {/* ── Row 3: Notes — full width ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
          <Fld label="Notes">
            <input
              className="ahp-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note…"
              disabled={!editMode}
              style={{ width: "100%" }}
            />
          </Fld>
        </div>

        {/* ── Emission / Energy section ── */}
        {(hasEmission || hasEnergy) && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
              <span style={{ fontSize: 9, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em" }}>CALCULATED VALUES</span>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
              {hasEmission && (
                <CompactTypeRow
                  icon="🌿"
                  label="Emission"
                  value={emissionVal}
                  unit={emissionUnit}
                  accentColor="#15803d"
                  bgColor="#f0fdf4"
                  borderColor="#bbf7d0"
                />
              )}
              {hasEnergy && (
                <CompactTypeRow
                  icon="⚡"
                  label="Energy"
                  value={energyVal}
                  unit={energyUnit}
                  accentColor="#b45309"
                  bgColor="#fffbeb"
                  borderColor="#fde68a"
                />
              )}
            </div>
          </>
        )}

        {(hasNOx || hasSOx || hasPPM) && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
              <span style={{ fontSize: 9, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em" }}>CALCULATED VALUES</span>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
              {hasNOx && (
                <CompactTypeRow
                  icon="💨"
                  label="NOx"
                  value={noxVal}
                  unit={noxUnit}
                  accentColor="#b91c1c"
                  bgColor="#fef2f2"
                  borderColor="#fecaca"
                />
              )}

              {hasSOx && (
                <CompactTypeRow
                  icon="🟡"
                  label="SOx"
                  value={soxVal}
                  unit={soxUnit}
                  accentColor="#a16207"
                  bgColor="#fefce8"
                  borderColor="#fef08a"
                />
              )}

              {hasPPM && (
                <CompactTypeRow
                  icon="🌫️"
                  label="PPM"
                  value={ppmVal}
                  unit={ppmUnit}
                  accentColor="#6b21a8"
                  bgColor="#faf5ff"
                  borderColor="#e9d5ff"
                />
              )}
            </div>
          </>
        )}

        {/* ── KPI Additional Forms (one per schema) ── */}
        {kpiFormSchemas.map((schema) => (
          <React.Fragment key={schema.kpi}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
              <span style={{ fontSize: 9, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em", display: "flex", alignItems: "center", gap: 5 }}>
                {schema.kpi} DATA
                {!editMode && (
                  <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--ink4)", fontSize: 9 }}>(read-only)</span>
                )}
              </span>
              <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
            </div>

            <KpiFormFields
              schema={schema.fields}
              kpiType={schema.kpi}
              formData={kpiFormData[schema.kpi] ?? {}}
              onChange={(key, newVal) =>
                setKpiFormData((p) => ({
                  ...p,
                  [schema.kpi]: { ...(p[schema.kpi] ?? {}), [key]: newVal },
                }))
              }
              readOnly={!editMode}
              variant="accordion"
              unitMaster={unitMaster}
            />
          </React.Fragment>
        ))}

        {/* ── Divider ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0" }}>
          <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
          <span style={{ fontSize: 9, color: "var(--ink4)", fontFamily: "var(--fm)", letterSpacing: ".06em" }}>DATA FIELDS</span>
          <div style={{ flex: 1, height: 1, background: "var(--bdr2)" }} />
        </div>

        {/* ── Document ── */}
        <div>
          <RequiredEvidences requiredEvidences={requiredEvidences} />
          <label className="ahp-lbl" style={{ marginBottom: 5, marginTop: 5 }}>
            {editMode ? "Evidence" : "Attached Documents"}
            {editMode && (
              <span style={{ fontSize: 9, fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--ink4)", marginLeft: 6 }}>
                XLSX auto-fills fields · PDF/DOC/image stored as proof
              </span>
            )}
          </label>
          {!editMode ? (
            answer.documents?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {answer.documents.map((doc, i) => (
                  <DocumentViewerPanel key={doc.id ?? i} document={doc} />
                ))}
              </div>
            ) : (
              <DocumentViewerPanel document={null} />
            )
          ) : (
            <>
              {uploadErr && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7, padding: "8px 12px", borderRadius: 7, background: "var(--red2)", border: "1px solid #fca5a5", fontSize: 11.5, color: "var(--red)", fontWeight: 600 }}>
                  <KpiIcon name="x" size={12} color="var(--red)" /> {uploadErr}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <MultiDocumentUploadField
                  existingDocuments={answer.documents || []}
                  resetKey={answer.id}
                  onChange={(next) => { setAttachments(next); setUploading(next.isUploading); }}
                />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, fontSize: 12, color: "var(--txt2, #475569)", cursor: "pointer" }}>
                <input type="checkbox" checked={shouldParse} onChange={(e) => setShouldParse(e.target.checked)} />
                Parse attached document(s) with AI
              </label>
              {!shouldParse && (
                <div style={{ fontSize: 11, color: "var(--txt3, #94a3b8)", marginTop: 2 }}>
                  The entered value will be trusted as-is — no AI extraction will run against these documents.
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Bottom save row (edit mode only) ── */}
        {editMode && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8, borderTop: "1px solid var(--bdr2)" }}>
            <button className="ahp-btn ahp-btn-p" onClick={save} disabled={saving || uploading}>
              {saving
                ? <><span className="ahp-spin" style={{ width: 12, height: 12, border: "2px solid #ffffff44", borderTopColor: "#fff" }} /> Saving…</>
                : <><KpiIcon name="check" size={13} color="#fff" /> Save Changes</>}
            </button>
            <button className="ahp-btn ahp-btn-g" onClick={cancelEdit}>Cancel</button>
            {msg && (
              <span style={{ fontSize: 11.5, fontWeight: 600, color: msg.ok ? "var(--green)" : "var(--red)" }}>
                {msg.ok ? "✓" : "⚠"} {msg.t}
              </span>
            )}
          </div>
        )}
      </div>

      {showHistModal && (
        <HistoryModal answer={answer} locs={locs} questionId={questionId} onClose={() => setShowHistModal(false)} />
      )}

      {showReturnModal && (
        <Modal show={showReturnModal} onClose={() => !returning && setShowReturnModal(false)} maxWidth={500}>
          <ModalHeader emoji="↩️" title="Return for Correction" subtitle="Provide a reason for returning this record to the contributor" onClose={() => !returning && setShowReturnModal(false)} />
          <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                Correction Comment *
              </div>
              <textarea
                className="audit-input"
                placeholder="Describe what needs to be corrected…"
                value={returnComment}
                onChange={(e) => setReturnComment(e.target.value)}
                rows={3}
                style={{ resize: "vertical", minHeight: 72, fontSize: 13, lineHeight: 1.6, width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--rule)" }}
              />
            </div>
            {returnError && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600 }}>
                ⚠️ {returnError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
              <SecondaryButton onClick={() => setShowReturnModal(false)} disabled={returning}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleReturnConfirm} disabled={returning || !returnComment.trim()} style={{ background: "var(--red)", borderColor: "var(--red)" }}>
                {returning ? "Returning…" : "Confirm Return"}
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AccordionPanel;